import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { useAuthStore } from '@/store/auth.store'
import { refundsService } from '@/services/refundsService'
import { vendorsService } from '@/services/vendorsService'
import { branchService } from '@/services/branchService'
import type { RefundVm, BranchEntity, Vendor } from '@/api/types'
import { Button } from '@/components/ui/button'
import { formatCalendarDate } from '@/lib/calendarDate'
import {
  formatDocNo,
  PurchasePrintLayout,
  type PrintLine,
  type PrintParty,
} from '@/modules/purchases/pages/PurchasePrintLayout'

function vendorParty(vendor: Vendor | null, cityName: string, countryName: string): PrintParty {
  const v = vendor as Vendor & Record<string, any> | null
  return {
    nameEn: v?.vendorNameEn || v?.vendorName || '-',
    nameAr: v?.vendorNameAr,
    address: v?.address,
    country: countryName || 'Saudi Arabia (SA)',
    city: cityName || '-',
    zip: v?.zipCode,
    phone: v?.phoneNo || v?.mobileNo,
    vat: v?.gstNo || v?.vatNo || v?.vatNumber,
    otherId: v?.companyRegistrationNo,
  }
}

function branchParty(branch: BranchEntity | null, cityName: string): PrintParty {
  return {
    nameEn: branch?.nameEn || '-',
    nameAr: branch?.nameAr,
    address: [branch?.addressEn, branch?.street].filter(Boolean).join(', '),
    addressAr: branch?.addressAr,
    country: branch?.countryName || 'Saudi Arabia (SA)',
    city: cityName || branch?.cityName || '-',
    zip: branch?.zipCode,
    phone: branch?.phone,
    vat: branch?.vatNo,
    otherId: branch?.crNo,
  }
}

export default function RefundsPrint() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refund, setRefund] = useState<RefundVm | null>(null)
  const [vendor, setVendor] = useState<Vendor | null>(null)
  const [branch, setBranch] = useState<BranchEntity | null>(null)
  const [vendorCity, setVendorCity] = useState('')
  const [vendorCountry, setVendorCountry] = useState('')
  const [branchCity, setBranchCity] = useState('')

  useEffect(() => {
    if (!branchId || !id) {
      setError('Missing branch or vendor credit identifier')
      setLoading(false)
      return
    }
    const refundId = Number(id)
    if (Number.isNaN(refundId)) {
      setError('Invalid vendor credit id')
      setLoading(false)
      return
    }

    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        const refundData = await refundsService.getRefundById(branchId, refundId)
        setRefund(refundData)
        const [vendorData, branchData] = await Promise.all([
          refundData.vendorId ? vendorsService.getVendorById(branchId, refundData.vendorId).catch(() => null) : null,
          branchService.getBranchById(branchId).catch(() => null),
        ])
        setVendor(vendorData)
        setBranch(branchData)

        if (branchData?.country && branchData?.city) {
          try {
            const cities = await branchService.getCityLookups(branchData.country)
            const city = cities.find((c: any) => c.id === branchData.city)
            if (city) setBranchCity(city.lookupNameEn || '')
          } catch {
            /* ignore */
          }
        }

        if (vendorData?.country) {
          try {
            const [countries, cities] = await Promise.all([
              vendorsService.getCountries(),
              vendorsService.getCities(vendorData.country),
            ])
            const country = countries.find((c) => c.id === vendorData.country)
            const city = cities.find((c) => c.id === vendorData.city)
            setVendorCountry(country?.lookupNameEn || '')
            setVendorCity(city?.lookupNameEn || '')
          } catch {
            /* ignore */
          }
        }
      } catch (err: any) {
        const message = err?.response?.data?.message || err?.message || 'Unable to load vendor credit details'
        setError(message)
        toast.error(message)
      } finally {
        setLoading(false)
      }
    }

    void load()
  }, [branchId, id])

  const lines: PrintLine[] = useMemo(() => {
    return (refund?.refundLine || []).map((line) => {
      const itemPrice = line.itemPrice || (line.qty || 0) * (line.unitPrice || 0)
      return {
        description: line.description,
        qty: line.qty,
        unitPrice: line.unitPrice,
        itemPrice,
        taxableAmount: itemPrice,
        taxRate: line.taxRate,
        taxAmount: line.taxAmount,
        lineAmount: line.lineAmount,
      }
    })
  }, [refund])

  const totals = useMemo(() => {
    const subTotal = lines.reduce((sum, line) => sum + (line.itemPrice ?? 0), 0)
    const taxableAmount = lines.reduce((sum, line) => sum + (line.taxableAmount ?? 0), 0)
    const vatAmount = lines.reduce((sum, line) => sum + (line.taxAmount ?? 0), 0)
    return {
      subTotal: refund?.subTotal || subTotal,
      taxableAmount,
      vatAmount: refund?.totalTax || vatAmount,
      total: refund?.total || taxableAmount + vatAmount,
      retentionPercent: refund?.retentionPercent || 0,
    }
  }, [refund, lines])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Preparing vendor credit for print...</p>
        </div>
      </div>
    )
  }

  if (error || !refund) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <p className="mb-4 text-lg font-semibold text-destructive">{error || 'Vendor credit not found'}</p>
        <Button onClick={() => navigate('/purchases/refunds')}>Back to Vendor Credits</Button>
      </div>
    )
  }

  return (
    <PurchasePrintLayout
      titleAr="اشعار دائن"
      titleEn="Credit Note"
      printButtonLabel="Print Credit Note"
      infoRows={[
        { en: 'Credit Note #:', ar: 'رقم الإشعار', value: formatDocNo('CN', refund.refundNo || refund.refundId) },
        { en: 'Credit Note Date:', ar: 'تاريخ الإشعار', value: formatCalendarDate(refund.refundDate) },
        { en: 'Bill #:', ar: 'رقم الفاتورة', value: formatDocNo('BILL', refund.billNo || refund.billId) },
        { en: 'Due Date:', ar: 'تاريخ الاستحقاق', value: formatCalendarDate(refund.dueDate) || '-' },
        { en: 'Reference:', ar: 'المرجع', value: refund.refNo || '-' },
      ]}
      seller={vendorParty(vendor, vendorCity, vendorCountry)}
      buyer={branchParty(branch, branchCity)}
      lines={lines}
      totals={totals}
      notes={refund.note || refund.refNo}
      footer="This credit note was generated electronically and does not require any signature. | تم إنشاء هذا الإشعار إلكترونيًا ولا يحتاج إلى توقيع."
      itemAmountLabelEn="ITEM AMT."
      itemAmountLabelAr="مبلغ البند"
    />
  )
}
